import React, { useEffect, useState } from 'react';
import { getPaiements, getPaiementStats, getPartenaires } from '../services/api';

const Paiements: React.FC = () => {
  const [paiements, setPaiements] = useState<any[]>([]);
  const [partenaires, setPartenaires] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<any>({});
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { getPartenaires().then(r => setPartenaires(r.data)).catch(() => {}); }, []);

  const buildParams = (extra?: Record<string, string>) => {
    const params: Record<string, string> = { ...extra };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v as string; });
    return params;
  };

  const loadData = () => {
    setRefreshing(true);
    Promise.all([
      getPaiementStats(buildParams()).then(r => setStats(r.data)).catch(() => {}),
      getPaiements(buildParams({ page: page.toString(), limit: '30' })).then(r => {
        setPaiements(r.data.data || []);
        setTotal(r.data.total || 0);
        setTotalPages(r.data.totalPages || 1);
      }).catch(() => {})
    ]).finally(() => setRefreshing(false));
  };

  // Reload stats when filters change
  useEffect(() => {
    getPaiementStats(buildParams()).then(r => setStats(r.data)).catch(() => {});
  }, [filters]);

  // Reload list when filters or page change
  useEffect(() => {
    getPaiements(buildParams({ page: page.toString(), limit: '30' })).then(r => {
      setPaiements(r.data.data || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.totalPages || 1);
    }).catch(() => {});
  }, [page, filters]);

  const updateFilter = (key: string, value: string) => {
    setFilters((prev: any) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const formatMontant = (v: number) => {
    if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
    if (v >= 1000) return (v / 1000).toFixed(0) + 'K';
    return v?.toString() || '0';
  };

  const moisNoms = ['', 'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

  // Extract unique types from stats
  const typePaiements = stats?.par_type?.map((t: any) => t.typepaiement).filter(Boolean) || [];

  // Top 3 types for cards
  const topTypes = stats?.par_type?.slice(0, 3) || [];
  const cardColors = ['bg-teal', 'bg-orange', 'bg-purple'];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ color: '#333', margin: 0 }}>Paiements ({total.toLocaleString('fr-FR')})</h2>
        <button
          onClick={loadData}
          disabled={refreshing}
          style={{
            padding: '8px 18px', borderRadius: 8, border: 'none',
            background: refreshing ? '#bdbdbd' : '#1976d2', color: '#fff',
            cursor: refreshing ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 6
          }}
        >
          <span style={{ display: 'inline-block', animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>&#8635;</span>
          {refreshing ? 'Actualisation...' : 'Actualiser'}
        </button>
      </div>

      {/* Stat Cards */}
      <div className="dashboard-cards">
        <div className="dash-card bg-blue">
          <span className="dash-card-icon">&#128179;</span>
          <h4>Total Paiements</h4>
          <div className="dash-card-value">{stats?.totalCount?.toLocaleString('fr-FR') || 0}</div>
        </div>
        {topTypes.map((t: any, i: number) => (
          <div key={t.typepaiement} className={`dash-card ${cardColors[i] || 'bg-green'}`}>
            <span className="dash-card-icon">{i === 0 ? '\u{1F4B3}' : i === 1 ? '\u{1F4B5}' : '\u{1F3E6}'}</span>
            <h4>{t.typepaiement} ({t.nombre?.toLocaleString('fr-FR')})</h4>
            <div className="dash-card-value">{formatMontant(t.total_montant)} FCFA</div>
          </div>
        ))}
        <div className="dash-card bg-green">
          <span className="dash-card-icon">&#128176;</span>
          <h4>Montant Total</h4>
          <div className="dash-card-value">{formatMontant(stats?.totalMontant || 0)} FCFA</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <select value={filters.partenaire_id || ''} onChange={e => updateFilter('partenaire_id', e.target.value)}>
          <option value="">Tous les partenaires</option>
          {partenaires.map(p => <option key={p.id} value={p.id}>{p.libelle}</option>)}
        </select>
        <select value={filters.typepaiement || ''} onChange={e => updateFilter('typepaiement', e.target.value)}>
          <option value="">Tous les types</option>
          {typePaiements.map((t: string) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filters.mois || ''} onChange={e => updateFilter('mois', e.target.value)}>
          <option value="">Tous les mois</option>
          {moisNoms.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
        <select value={filters.annee || ''} onChange={e => updateFilter('annee', e.target.value)}>
          <option value="">Toutes les annees</option>
          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <input
          type="date"
          value={filters.date_debut || ''}
          onChange={e => updateFilter('date_debut', e.target.value)}
          title="Date debut"
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}
        />
        <input
          type="date"
          value={filters.date_fin || ''}
          onChange={e => updateFilter('date_fin', e.target.value)}
          title="Date fin"
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}
        />
        {Object.values(filters).some(v => v) && (
          <button
            onClick={() => { setFilters({}); setPage(1); }}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#ef5350', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            Reinitialiser
          </button>
        )}
      </div>

      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Reference</th>
              <th>Abonne</th>
              <th>Partenaire</th>
              <th>Type</th>
              <th>Montant (FCFA)</th>
            </tr>
          </thead>
          <tbody>
            {paiements.map((p: any, i: number) => (
              <tr key={p.id || i}>
                <td>{p.datepaiement ? new Date(p.datepaiement).toLocaleDateString('fr-FR') : '-'}</td>
                <td><strong>{p.reference || '-'}</strong></td>
                <td>{p.abonne_nom?.trim() || p.abonne?.trim() || p.prenomnom?.trim() || p.abonne_ref || '-'}</td>
                <td>{p.partenaire || '-'}</td>
                <td>
                  <span style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                    background: p.typepaiement === 'Espece' ? '#e8f5e9' : p.typepaiement === 'wave' ? '#e3f2fd' : '#fff3e0',
                    color: p.typepaiement === 'Espece' ? '#2e7d32' : p.typepaiement === 'wave' ? '#1565c0' : '#e65100'
                  }}>
                    {p.typepaiement || '-'}
                  </span>
                </td>
                <td>{p.montant?.toLocaleString('fr-FR')} FCFA</td>
              </tr>
            ))}
            {paiements.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucun paiement trouve</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Precedent</button>
        <span style={{ padding: '8px 14px', fontSize: 14 }}>Page {page} / {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Suivant</button>
      </div>
    </div>
  );
};

export default Paiements;
